export interface LabeledValue {
    label: any;
    value: any;
}

/**
 * Replaces all {value, label} objects by returning the value
 *
 * @param source - The object you want to parse.
 * @returns A new object without {value, label} objects.
 */
export function parseObject(source: any): any {
    if (Array.isArray(source)) {
        return source.map((item) => (typeof item === 'object' && item !== null ? ('label' in item && 'value' in item ? item.value : parseObject(item)) : item));
    } else if (typeof source === 'object' && source !== null) {
        return Object.fromEntries(
            Object.entries(source)
                .filter(([key]) => Object.prototype.hasOwnProperty.call(source, key))
                .map(([key, value]) => [
                    key,
                    typeof value === 'object' && value !== null ? ('label' in value && 'value' in value ? value.value : parseObject(value)) : value
                ])
        );
    } else {
        return source;
    }
}

/**
 * Replaces all {value, label} objects by returning the value
 *
 * @param obj - The object you want to parse.
 * @returns A new object without null values.
 */
export function removeNullValues(obj: { [key: string]: any }): { [key: string]: any } {
    const newObj: { [key: string]: any } = {};
    for (const key in obj) {
        if (obj[key] !== null) {
            newObj[key] = obj[key];
        }
    }
    return newObj;
}

/**
 * Removes all keys from an object except for the specified ones.
 *
 * @param obj - The object to filter.
 * @param keepKeys - An array of keys to keep in the resulting object.
 * @returns A new object containing only the specified keys and their values.
 */
export function removeExcept<T extends Record<string, unknown>>(obj: T, keepKeys: string[]): Partial<T> {
    const filteredObj: Partial<T> = {};
    for (const key in obj) {
        if (keepKeys.includes(key)) {
            filteredObj[key] = obj[key];
        }
    }
    return filteredObj;
}

export function recursiveSearch<T extends { name: string; children?: T[] }>(searchString: string, data: T[]): T[] {
    const results: T[] = [];

    const search = (items: T[]) => {
        for (const item of items) {
            if (item.name.toLowerCase().includes(searchString.toLowerCase())) {
                results.push(item);
            } else if (item.children) {
                search(item.children);
            }
        }
    };

    search(data);
    return results;
}

export function compareObjectsByKeyAndValue(obj1: Record<string, any>, obj2: Record<string, any>): boolean {
    return Object.keys(obj1).every((key) => obj2.hasOwnProperty(key) && obj1[key] === obj2[key]);
}

export function checkNullFields(obj1: Record<string, any>, obj2: Record<string, any>): string[] {
    const keys: string[] = [];
    Object.keys(obj1).forEach((key) => {
        if (obj2.hasOwnProperty(key)) {
            if (obj1[key] === null && obj2[key] !== null) {
                keys.push(key);
            } else if (typeof obj1[key] === 'number' && isNaN(obj1[key]) && !isNaN(obj2[key])) {
                keys.push(key);
            } else if (typeof obj1[key] === 'string' && obj1[key] === '' && obj2[key] !== '') {
                keys.push(key);
            }
        }
    });
    return keys;
}

export function hasDuplicateValue<T>(array: T[], key: keyof T): boolean {
    const seenValues = new Set();
    for (const obj of array) {
        const value = obj[key];
        if (seenValues.has(value)) {
            return true;
        }
        seenValues.add(value);
    }
    return false;
}

export function compareArrays(arr1: string[], arr2: string[]): { add: string[]; remove: string[] } {
    const add = [];
    const remove = [];
    for (const value of arr1) {
        if (!arr2.includes(value)) {
            remove.push(value);
        }
    }
    for (const value of arr2) {
        if (!arr1.includes(value)) {
            add.push(value);
        }
    }
    return { add, remove };
}
